import React, { useState, useEffect } from 'react';
import MobileCostsForm from "./SalesMobilePhones";
import CostComparisonComponent from './CostComparisonComponent';
import mobileService from '../services/mobileService';

const CurrentMobileCosts = ({ 
  selectedConnectionId, 
  onMobileDataUpdate,
  charges,
  totalAllCalls,
  totalMonthlyCost,
  isHostedLifetimeLicenses,
  leasedLines
}) => {
  const [data, setData] = useState({
    currentMobiles: 0,
    currentConnections: 0,
    currentSpend: 0,
    monthsLeft: 0,
    savings: 0,
    mobileTypes: {
      directorPremium: 0,
      director: 0,
      manager: 0,
      standard: 0,
    },
    takeOverImmediately: 1,
  });
  
  const [fieldsEnabled, setFieldsEnabled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [mobileData, setMobileData] = useState(null);
  const [hasMobileData, setHasMobileData] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Fetch existing mobile data on component mount
  useEffect(() => {
    const fetchMobileData = async () => {
      if (selectedConnectionId) {
        try {
          //console.log('Fetching mobile data for connectionId:', selectedConnectionId);
          const existingMobileData = await mobileService.getMobileService(selectedConnectionId);
          
          if (existingMobileData && existingMobileData.id) {
           // console.log('Found existing mobile data:', existingMobileData);
            setMobileData(existingMobileData);
            setHasMobileData(true);
            
            // Populate the form data from existing data
            const existingCosts = parseFloat(existingMobileData.existing_costs) || 0;
            const lineRentals = parseFloat(existingMobileData.existing_line_rentals_service_charges) || 0;
            const total = existingCosts + lineRentals;
            const savings_total = total * 0.2;

            const staffCounts = {};
            existingMobileData.staff_levels?.forEach(level => {
              staffCounts[level.staff_level] = level.count;
            });

            setData({
              currentMobiles: total,
              currentConnections: existingMobileData.existing_sims_to_migrate || 0,
              currentSpend: total,
              monthsLeft: 0, // You might want to calculate this from contract_end_date
              savings: savings_total,
              mobileTypes: {
                directorPremium: staffCounts.director_premium || 0,
                director: staffCounts.director || 0,
                manager: staffCounts.manager || 0,
                standard: staffCounts.standard || 0,
              },
              takeOverImmediately: existingMobileData.take_mobiles_over_immediately ? 1 : 0,
            });

            setFieldsEnabled(true);
          } else {
            console.log('No existing mobile data found');
            setHasMobileData(false);
            setFieldsEnabled(false);
          }
        } catch (error) {
          console.error('Error fetching mobile data:', error);
          setHasMobileData(false);
          setFieldsEnabled(false);
        }
      }
    };

    fetchMobileData();
  }, [selectedConnectionId]);

  const handleChange = (e, key, subKey = null) => {
    if (!fieldsEnabled) return;
    
    const value = parseFloat(e.target.value) || 0;
    if (subKey) {
      setData((prev) => ({
        ...prev,
        [key]: { ...prev[key], [subKey]: value },
      }));
    } else {
      setData((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleConfigureMobileCosts = () => {
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEditMobileCosts = () => {
    setIsEditing(true);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
  };

  const handleSaveSuccess = (updatedData) => {
    //console.log("Saved mobile data:", updatedData);
  
    setMobileData(updatedData);
    setHasMobileData(true);
    onMobileDataUpdate(updatedData);
    
    const existingCosts = parseFloat(updatedData.existing_costs) || 0;
    const lineRentals = parseFloat(updatedData.existing_line_rentals_service_charges) || 0;
    const total = existingCosts + lineRentals;
    const savings_total = total * 0.2;

    const staffCounts = {};
    updatedData.staff_levels?.forEach(level => {
      staffCounts[level.staff_level] = level.count;
    });

    setData(prev => ({
      ...prev,
      currentMobiles: total,
      currentConnections: updatedData.existing_sims_to_migrate,
      currentSpend: total,
      savings: savings_total,
      mobileTypes: {
        directorPremium: staffCounts.director_premium || 0,
        director: staffCounts.director || 0,
        manager: staffCounts.manager || 0,
        standard: staffCounts.standard || 0,
      },
      takeOverImmediately: updatedData.take_mobiles_over_immediately ? 1 : 0,
    }));

    setFieldsEnabled(true);
    setShowModal(false);
    setIsEditing(false);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white border border-gray-300 rounded-md">
      {!hasMobileData ? (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md text-center">
          <p className="mb-4">Please configure mobile costs first</p>
          <button
            onClick={handleConfigureMobileCosts}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Configure Mobile Costs
          </button>
        </div>
      ) : (
        <div className="mb-6 flex justify-end space-x-4">
          <button
            onClick={handleEditMobileCosts}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Mobile Details
          </button>
        </div>
      )}
     
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start text-sm">
        {/* Left Column - Current Provider Mobiles */}
        <div>
          <h2 className="text-lg font-bold mb-2">Current Provider Mobiles</h2>
          <div className="border border-gray-300">
            <div className="flex justify-between px-3 py-2 border-b border-gray-200">
              <span>Current Mobiles</span>
              <input
                type="number"
                value={data.currentMobiles}
                onChange={(e) => handleChange(e, 'currentMobiles')}
                className={`text-right w-20 border-none outline-none ${!fieldsEnabled ? 'bg-gray-100' : ''}`}
                disabled={!fieldsEnabled}
              />
            </div>
            <div className="flex justify-between px-3 py-2 border-b border-gray-200">
              <span>Current Connections</span>
              <input
                type="number"
                value={data.currentConnections}
                onChange={(e) => handleChange(e, 'currentConnections')}
                className={`text-right w-20 border-none outline-none ${!fieldsEnabled ? 'bg-gray-100' : ''}`}
                disabled={!fieldsEnabled}
              />
            </div>
            <div className="flex justify-between px-3 py-2 border-b border-gray-200">
              <span>Current spend</span>
              <input
                type="text"
                value={`£${data.currentSpend.toFixed(2)}`}
                onChange={(e) => handleChange(e, 'currentSpend')}
                className={`text-right w-20 border-none outline-none ${!fieldsEnabled ? 'bg-gray-100' : ''}`}
                disabled={!fieldsEnabled}
              />
            </div>
            <div className="flex justify-between px-3 py-2">
              <span>Months left on contract</span>
              <input
                type="number"
                value={data.monthsLeft}
                onChange={(e) => handleChange(e, 'monthsLeft')}
                className={`text-right w-20 border-none outline-none ${!fieldsEnabled ? 'bg-gray-100' : ''}`}
                disabled={!fieldsEnabled}
              />
            </div>
          </div>
        </div>

        {/* Right Column - Mobile Types */}
        <div>
          <h2 className="text-lg font-bold mb-2">Mobile Type When Connected</h2>
          <div className="border border-gray-300">
            <div className="flex justify-between px-3 py-2 border-b border-gray-200">
              <span>Director Premium level</span>
              <input
                type="number"
                value={data.mobileTypes.directorPremium}
                onChange={(e) => handleChange(e, 'mobileTypes', 'directorPremium')}
                className={`text-right w-20 border-none outline-none ${!fieldsEnabled ? 'bg-gray-100' : ''}`}
                disabled={!fieldsEnabled}
              />
            </div>
            <div className="flex justify-between px-3 py-2 border-b border-gray-200">
              <span>Director level</span>
              <input
                type="number"
                value={data.mobileTypes.director}
                onChange={(e) => handleChange(e, 'mobileTypes', 'director')}
                className={`text-right w-20 border-none outline-none ${!fieldsEnabled ? 'bg-gray-100' : ''}`}
                disabled={!fieldsEnabled}
              />
            </div>
            <div className="flex justify-between px-3 py-2 border-b border-gray-200">
              <span>Manager level</span>
              <input
                type="number"
                value={data.mobileTypes.manager}
                onChange={(e) => handleChange(e, 'mobileTypes', 'manager')}
                className={`text-right w-20 border-none outline-none ${!fieldsEnabled ? 'bg-gray-100' : ''}`}
                disabled={!fieldsEnabled}
              />
            </div>
            <div className="flex justify-between px-3 py-2">
              <span>Standard level</span>
              <input
                type="number"
                value={data.mobileTypes.standard}
                onChange={(e) => handleChange(e, 'mobileTypes', 'standard')}
                className={`text-right w-20 border-none outline-none ${!fieldsEnabled ? 'bg-gray-100' : ''}`}
                disabled={!fieldsEnabled}
              />
            </div>
          </div>
        </div>
      </div>
      
      {fieldsEnabled && data.takeOverImmediately === 0 && (
        <div className="mt-6 border border-gray-300 p-4 text-sm flex justify-between font-semibold">
          <span>Cashback expected savings to be provided until current provider contract end date</span>
          <span>£0</span>
        </div>
      )}
      
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  {isEditing ? 'Edit Mobile Details' : 'Mobile Details'}
                </h3>
                <button 
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <MobileCostsForm 
                selectedConnectionId={selectedConnectionId}
                onSaveSuccess={handleSaveSuccess}
                onCancel={handleCloseModal}
                initialData={isEditing ? mobileData : null}
                isEditing={isEditing}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentMobileCosts;